import { useEffect, useId, useRef, useState } from 'react'
import { useLang } from '../i18n'
import type { AgentLockAsk, AgentLockChoice, AgentUndoneTarget } from '../lib/api'

/** The ask timeout (docs/spec/30-coediting.md "Defaults"): no answer → Ignore. The passerelle
 *  enforces it too; this side only makes sure the card never outlives it. */
export const LOCK_ASK_TIMEOUT_MS = 120_000

/**
 * A paused run's question about the targets that stayed locked (lock conflicts, AB#303): each
 * target with who holds it, and Ignore / Retry / Force. Force is offered only when a member — not
 * an agent — holds a target, and asks a one-line confirmation first. Past the deadline the card
 * answers Ignore by itself, exactly once.
 */
export function LockAskCard({ ask, onAnswer }: { ask: AgentLockAsk; onAnswer: (choice: AgentLockChoice) => void }) {
  const { t } = useLang()
  const titleId = useId()
  const [confirming, setConfirming] = useState(false)
  const answered = useRef(false)
  const cancelRef = useRef<HTMLButtonElement>(null)
  const forceRef = useRef<HTMLButtonElement>(null)
  const onAnswerRef = useRef(onAnswer)
  onAnswerRef.current = onAnswer

  const answer = (choice: AgentLockChoice): void => {
    if (answered.current) return
    answered.current = true
    onAnswerRef.current(choice)
  }

  // One timer per question: at its deadline (capped at the ask timeout), Ignore.
  useEffect(() => {
    answered.current = false
    setConfirming(false)
    const left = Date.parse(ask.expiresAt) - Date.now()
    const ms = Number.isFinite(left) ? Math.min(Math.max(0, left), LOCK_ASK_TIMEOUT_MS) : LOCK_ASK_TIMEOUT_MS
    const h = setTimeout(() => answer('ignore'), ms)
    return () => clearTimeout(h)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-armed per question, not per render
  }, [ask.id])

  // Keyboard users land on the confirmation's safe choice, and back on Force if they cancel.
  const wasConfirming = useRef(false)
  useEffect(() => {
    if (confirming) cancelRef.current?.focus()
    else if (wasConfirming.current) forceRef.current?.focus()
    wasConfirming.current = confirming
  }, [confirming])

  const canForce = ask.choices.includes('force')
  const members = [...new Set(ask.targets.filter((tg) => tg.kind === 'human').map((tg) => tg.holder.name))]

  return (
    <section className="chat-lock-ask" aria-labelledby={titleId}>
      <p id={titleId} className="chat-lock-ask-title">{t('lock_ask_title')}</p>
      <ul className="chat-lock-ask-targets">
        {ask.targets.map((tg) => (
          <li key={tg.key}>
            <span className="chat-lock-ask-target">{tg.label}</span>
            {' — '}
            {(tg.kind === 'human' ? t('lock_ask_member') : t('lock_ask_agent')).replace('{name}', () => tg.holder.name)}
          </li>
        ))}
      </ul>
      {confirming ? (
        <div className="chat-lock-ask-actions">
          <p className="chat-lock-ask-confirm">{t('lock_ask_force_confirm').replace('{names}', () => members.join(', '))}</p>
          <button type="button" className="mini danger" onClick={() => answer('force')}>
            {t('lock_ask_force_yes')}
          </button>
          <button type="button" ref={cancelRef} className="mini no" onClick={() => setConfirming(false)}>
            {t('lock_ask_cancel')}
          </button>
        </div>
      ) : (
        <div className="chat-lock-ask-actions">
          <button type="button" className="mini no" onClick={() => answer('ignore')}>
            {t('lock_ask_ignore')}
          </button>
          <button type="button" className="mini ok" onClick={() => answer('retry')}>
            {t('lock_ask_retry')}
          </button>
          {canForce && (
            <button type="button" ref={forceRef} className="mini danger" onClick={() => setConfirming(true)}>
              {t('lock_ask_force')}
            </button>
          )}
        </div>
      )}
      <p className="chat-lock-ask-hint">{t('lock_ask_timeout')}</p>
    </section>
  )
}

/** The final answer's "Left undone" list: each target, who held it, and why it was not done. */
export function UndoneList({ undone }: { undone: AgentUndoneTarget[] }) {
  const { t } = useLang()
  const titleId = useId()
  const why = (u: AgentUndoneTarget): string =>
    (u.reason === 'ignored' ? t('lock_undone_ignored') : u.reason === 'no_answer' ? t('lock_undone_no_answer') : t('lock_undone_still')).replace(
      '{name}',
      () => u.holder,
    )
  return (
    <div className="chat-undone">
      <p id={titleId} className="chat-undone-title">{t('lock_undone_title')}</p>
      <ul aria-labelledby={titleId}>
        {undone.map((u, i) => (
          <li key={`${u.label}-${i}`}>
            <span className="chat-lock-ask-target">{u.label}</span> — {why(u)}
          </li>
        ))}
      </ul>
    </div>
  )
}
